import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardFooter,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';

import Breadcrumbs from '@Components/Breadcrumbs';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import service from '@Service';
import { evalKeys, useEvalSuites } from '@Hooks/useEvals';
import { useSouls } from '@Hooks/useSouls';

const FormSchema = z.object({
  name: z.string().min(1, 'Name is required.'),
  description: z.string().optional(),
  defaultSoulId: z.string().optional(),
});

type FormValues = z.infer<typeof FormSchema>;

export default function CreateEvalSuite() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: souls } = useSouls();
  // intentionally invalidating both filtered and unfiltered eval-suite keys
  useEvalSuites({ enabled: false });

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: { name: '', description: '', defaultSoulId: '' },
  });

  const onSubmit = async (values: FormValues) => {
    form.clearErrors('root');
    try {
      const suite = await service.createEvalSuite({
        name: values.name,
        description: values.description ? values.description : null,
        defaultSoulId: values.defaultSoulId ? values.defaultSoulId : null,
      });
      await queryClient.invalidateQueries({ queryKey: evalKeys.suites });
      toastSuccess('Suite created.');
      navigate(`/evals/${suite.id}`);
    } catch (error) {
      console.error('Error creating eval suite:', error);
      form.setError('root', {
        type: 'server',
        message: 'Unable to create suite.',
      });
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'AI', href: '/evals' },
          { name: 'Evals', href: '/evals' },
          { name: 'Create', href: '/evals/create' },
        ]}
      />
      <div className="mt-8 mb-4">
        <Heading>Create Eval Suite</Heading>
      </div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {rootError && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          )}
          <Card>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g. Greeting smoke test" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="What does this suite verify?"
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Optional.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="defaultSoulId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Soul</FormLabel>
                    <FormControl>
                      <select
                        className="border-input bg-background ring-offset-background flex h-9 w-full rounded-md border px-3 py-1 text-sm"
                        {...field}
                      >
                        <option value="">— none —</option>
                        {(souls ?? []).map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormDescription>
                      Pre-selected when running the suite from the UI.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
            <CardFooter className="flex justify-end gap-2">
              <Button variant="link" asChild>
                <Link to="/evals">Cancel</Link>
              </Button>
              <Button type="submit" loading={form.formState.isSubmitting}>
                Create
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}
